import TitleMenu from 'components/Navigation/TitleMenu'
import Breadcrumb from 'components/Navigation/Breadcrumb'
import { useForm, type FieldValues } from 'react-hook-form'
import useTranslation from 'utils/hooks/useTranslation'
import HeatMode from './StepHome/HeatMode'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CoolMode, { type CoolModeRef } from './StepHome/CoolMode'
import Input, { InputType } from 'components/shared/inputs/input'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import NeedHelp from 'components/NeedHelp'
import NavigationButtons from 'components/NavigationButtons'
import { addNotification } from 'store/componentsSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationSchemaForForms, type FormValues } from 'store/projectSlice'
import { useSalesNetwork } from 'utils/hooks/useSalesNetworks'
import useCustomForm from 'utils/hooks/useCustomForm'
import { useProject } from 'utils/hooks/useProject'
import { defaultForm as defaultHeatForm } from 'store/heatModeSlice'
import { defaultForm as defaultCoolForm } from 'store/coolModeSlice'

const ProjectStepHome: React.FC = () => {
  // const [coolMode, _setCoolMode] = useState(false)
  const coolModeSubmitRef = useRef<CoolModeRef | null>(null)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { setCoolMode, updateForm } = useProject();
  const heatModeformStore = useAppSelector((state) => state.project.project.forms.heatModeStep)
  const coolModeformStore = useAppSelector((state) => state.project.project.forms.coolModeStep)
  const coolModeOpen = useAppSelector((state) => state.project.project.simulationCool)
  const projectStore = useAppSelector((state) => state.project);


  const { network } = useSalesNetwork()
  const enableCool = network?._parameters.enableCool;
  const [disableNavigarion, setDisableNavigarion] = useState<boolean>(false);

  const {
    control,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors, isValid },
    getFieldState,
    onSelect,
    trigger,
  } = useCustomForm<FormValues>({
    defaultValues: useMemo(() => {
      return {
        heatMode: heatModeformStore.form ?? defaultHeatForm,
        coolMode: coolModeformStore.form ?? defaultCoolForm,
        simulationCool: false,
      }
    }, [heatModeformStore.form, coolModeformStore.form]),
    resolver: yupResolver(validationSchemaForForms),
    mode: 'all',
  })

  const [heatMode_zones] = watch([
    'heatMode.zones',
  ])

  const submit = async () => {
    //NOSONAR
    const promise = new Promise((resolve) => {
      resolve(undefined)
    })

    const isHeatModeValid = await trigger('heatMode')

    // Only validate coolMode if it's enabled
    let isCoolModeValid = true
    if (coolModeOpen) {
      isCoolModeValid = await trigger('coolMode')
    }

    if (!isHeatModeValid || (coolModeOpen && !isCoolModeValid)) {
      // debugger;
      dispatch(
        addNotification({
          type: 'error',
          message: t('SOME_FIELDS_NEED_YOUR_ATTENTION'),
        }),
      )
      return Promise.reject(new Error('Validation failed'))
    }

    // If coolMode on, we have to check emitter type
    if (coolModeOpen && isCoolModeValid) {
      let coolModeOk = false
      heatMode_zones?.forEach((zone) => {
        if (zone.emetteur != 4 && zone.emetteur != 6 && zone.emetteur != 7) coolModeOk = true
      })

      if (!coolModeOk) {
        dispatch(addNotification({
          type: 'error',
          message: t('COOL_MODE_EMITTER_ERROR'),
        }))
        return Promise.reject(new Error(t('COOL_MODE_EMITTER_ERROR'))) // NOSONAR
      }
    }

    //let showProgressBar = false

    // if (deperdsLoading != null) {
    //   showProgressBar = true
    //   //UIStore.loadingContinuousStart()
    //   promise = deperdsLoading
    // } else {
    //   promise = Promise.resolve()
    // }

    return promise
      .then(async () => {
        const values = getValues();
        updateForm('heatModeStep', { form: values.heatMode });

        // Call CoolMode submit function if submit function exists
        if (coolModeOpen) {
          if (coolModeSubmitRef.current && typeof coolModeSubmitRef.current.submit === 'function') {
            try {
              await coolModeSubmitRef.current.submit()
            } catch (error) {
              throw error
            }
          } else {
            updateForm('coolModeStep', { form: values.coolMode });
          }
        }

        return Promise.resolve()
      })
      .catch((err) => {
        if (err?.coolModeEmitterError) {
          dispatch(
            addNotification({
              type: 'error',
              message: err.msg,
            }),
          )
        } else {
          dispatch(
            addNotification({
              type: 'error',
              message: t('SOME_FIELDS_NEED_YOUR_ATTENTION'),
            }),
          )
        }
        return Promise.reject(err)
      })
  }

  const onCoolModeChange = useCallback(async () => {
    const isHeatModeValid = await trigger('heatMode');


    // If coolMode on, we have to check emitter type
    if (!coolModeOpen && isHeatModeValid) {
      let coolModeOk = false
      heatMode_zones?.forEach((zone) => {
        if (zone.emetteur != 4 && zone.emetteur != 6 && zone.emetteur != 7) coolModeOk = true
      })

      if (!coolModeOk) {
        dispatch(addNotification({
          type: 'error',
          message: t('COOL_MODE_EMITTER_ERROR'),
        }))
        setCoolMode(false);
        return;
      }
    }

    if (!isHeatModeValid) {
      setCoolMode(false);
      return;
    }
    setCoolMode(!coolModeOpen);
  }, [coolModeOpen, setCoolMode])

  return (
    <>
      <TitleMenu title={projectStore.project.id ? projectStore.project.header.projectRef : t('MMENU_NEW_SIMULATION')} />
      <Breadcrumb handleSubmit={submit} />

      <group data-direction="column" data-align="center" data-height="autofit">
        <group data-space="20" data-gap="20" data-max-length="850" data-direction="column">
          <group data-align="center" data-space-vertical="20">
            <text data-weight="400" data-text-size="large" data-ellipsis="" data-color="grey-color-dark">
              <i data-space-horizontal="5" data-color="red">
                *
              </i>{' '}
              {t('REQUIRED_FIELDS')}
            </text>
          </group>

          <HeatMode
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            setDisableNavigarion={setDisableNavigarion}
            heatModeformStore={heatModeformStore}
            reset={reset}
            getFieldState={getFieldState}
            errors={errors}
            onSelect={onSelect}
            trigger={trigger}
          />

          {enableCool && (
            <group data-align="center" data-space-vertical="20" data-text-size="22">
              <group data-gap="10">
                <Input
                  type={InputType.Checkbox}
                  name="simulationCool"
                  control={control}
                  checkboxProps={{ 'data-lable-position': 'left', 'data-gap': '10' }}
                  label={t('COOL_SELECT')}
                  simple
                  onChange={onCoolModeChange}
                  checked={coolModeOpen}
                />
              </group>
            </group>
          )}

          {enableCool && coolModeOpen && (
            <CoolMode
              ref={coolModeSubmitRef}
              control={control}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              coolModeformStore={coolModeformStore}
              reset={reset}
              getFieldState={getFieldState}
              errors={errors}
            />
          )}

          <NavigationButtons handleSubmit={submit} hidePrev={true} maxLength={850} horisontalSpace={10} disabled={disableNavigarion} />
        </group>
      </group>

      <NeedHelp />
    </>
  )
}

export default ProjectStepHome
